import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Grid,
  Button,
  IconButton,
  Tooltip,
  Avatar,
  Stack,
  Divider,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Refresh,
  Visibility,
  TrendingUp,
  TrendingDown,
  Euro,
  AttachMoney,
  Business,
  Person,
  AccountBalance,
  Receipt,
  Add,
  Search,
  Image,
  PendingActions,
  CheckCircle,
  Cancel,
  CreditCard,
  Flip,
  Star,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import UserService from '../../services/userService';
import LimitService from '../../services/limitService';
import CreditService from '../../services/creditService';
import CreditRiskService from '../../services/creditRiskService';
import CheckImage from '../../components/credit/CheckImage';
import { INCOMING_PAYMENT_APPROVED_EVENT } from '../../utils/eventBus';
import useResolvedUserId from '../../hooks/useResolvedUserId';

const Credits = ({ user }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const currentUserId = useResolvedUserId(user);
  const [customerId, setCustomerId] = useState(user?.userId?.toString() || '');
  const [customerName, setCustomerName] = useState('');
  const [customerLimit, setCustomerLimit] = useState('');
  const [availableLimit, setAvailableLimit] = useState('');
  const [credits, setCredits] = useState([]);
  const [creditsWithRisks, setCreditsWithRisks] = useState([]);
  const [risks, setRisks] = useState([]);
  const [tempIncomingCredits, setTempIncomingCredits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [checkDialog, setCheckDialog] = useState(false);
  const [selectedCredit, setSelectedCredit] = useState(null);
  const [searchSeller, setSearchSeller] = useState('');
  const [modalTab, setModalTab] = useState(0); // 0: View, 1: Invoice
  const [isCheckFlipped, setIsCheckFlipped] = useState(false);
  const [selectedCustomerForTransfer, setSelectedCustomerForTransfer] = useState(null);
  const [isTransferring, setIsTransferring] = useState(false);
  const [hasTransferHistory, setHasTransferHistory] = useState(false);
  const [guaranteeFilter, setGuaranteeFilter] = useState('all'); // 'all', 'guaranteed', 'non-guaranteed'
  const checkImageRef = useRef(null);

  // Tab change handler
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Get customer name by ID
  const fetchCustomerName = async (id) => {
    if (!id) return;
    
    const result = await UserService.getCustomerNameByUserId(id);
    
    if (result.success) {
      setCustomerName(result.data.customerName);
    } else {
      setCustomerName('');
      setError(result.message);
    }
  };

  // Get customer limit
  const fetchCustomerLimit = async (id) => {
    if (!id) return;
    
    const result = await LimitService.getLimitByBuyerId(id);
    
    if (result.success && result.data) {
      setCustomerLimit(formatCurrency(result.data.amount, result.data.currency));
      setAvailableLimit(formatCurrency(result.data.remainingAmount, result.data.currency));
    } else {
      setCustomerLimit('N/A');
      setAvailableLimit('N/A');
    }
  };

  // Get credits by buyer
  const fetchCredits = async (id) => {
    if (!id) return;
    
    setLoading(true);
    setError('');
    
    const result = await CreditService.getCreditsByBuyer(id);
    
    if (result.success) {
      setCredits(result.data);
      
      // Fetch risks for each credit (silently fail if API is unavailable)
      // This prevents page loading from being blocked by risk API failures
      const creditsWithRiskData = await Promise.all(
        result.data.map(async (credit) => {
          try {
            const riskResult = await CreditRiskService.getRisksByCreditId(credit.id);
            return {
              ...credit,
              risks: riskResult.success ? riskResult.data : []
            };
          } catch (error) {
            // Silently fail - return credit without risk data
            // Don't log network errors to avoid console spam when backend is unavailable
            return {
              ...credit,
              risks: []
            };
          }
        })
      );
      
      setCreditsWithRisks(creditsWithRiskData);
      
      // Fetch all risks for statistics (silently fail if unavailable)
      try {
        const allRisksResult = await CreditRiskService.getRisksByUserId(id);
        if (allRisksResult.success) {
          setRisks(allRisksResult.data || []);
        } else {
          setRisks([]);
        }
      } catch (error) {
        // Silently fail - statistics can work without risk data
        console.warn('Failed to fetch all risks for statistics:', error);
        setRisks([]);
      }
    } else {
      setError(result.message);
      setCredits([]);
      setCreditsWithRisks([]);
      setRisks([]);
    }
    
    setLoading(false);
  };

  // Fetch all customer data
  const fetchCustomerInfo = async (id) => {
    if (!id) return;
    
    await Promise.all([
      fetchCustomerName(id),
      fetchCustomerLimit(id),
      fetchCredits(id)
    ]);
  };


  // Format currency
  const formatCurrency = (amount, currency = 'EUR') => {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    
    const parts = formatter.formatToParts(amount);
    let formatted = '';
    
    for (const part of parts) {
      if (part.type === 'currency') {
        formatted += part.value + ' ';
      } else {
        formatted += part.value;
      }
    }
    
    return formatted.trim();
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      case 'closed':
        return 'default';
      default:
        return 'default';
    }
  };

  // Get status text in Turkish
  const getStatusText = (status) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'Active';
      case 'pending':
        return 'Pending';
      case 'rejected':
        return 'Rejected';
      case 'closed':
        return 'Closed';
      default:
        return status;
    }
  };

  // Get risk type text
  const getRiskTypeText = (type) => {
    switch (type) {
      case 1:
        return 'Payment Guarantee';
      case 2:
        return 'Overdraft';
      default:
        return '-';
    }
  };

  // Get risk statistics
  const getRiskStats = () => {
    const active = risks.filter(r => r.status === 1);
    const closed = risks.filter(r => r.status === 2);
    const total = risks.length;
    const totalAmount = risks.reduce((sum, r) => sum + (r.riskAmount || 0), 0);
    const activeAmount = active.reduce((sum, r) => sum + (r.riskAmount || 0), 0);

    return { active: active.length, closed: closed.length, total, totalAmount, activeAmount };
  };

  // Filter credits by status, search, and guarantee filter
  const getFilteredCredits = () => {
    const sourceCredits = [...tempIncomingCredits, ...credits];
    let filtered = [];
    if (activeTab === 0) {
      // Pending tab
      filtered = sourceCredits.filter(c => c.status?.toLowerCase() === 'pending');
    } else if (activeTab === 1) {
      // Approved/Active tab
      filtered = sourceCredits.filter(c => c.status?.toLowerCase() === 'active');
    } else {
      // Rejected tab
      filtered = sourceCredits.filter(c => c.status?.toLowerCase() === 'rejected');
    }
    
    // Apply guarantee filter
    if (guaranteeFilter === 'guaranteed') {
      filtered = filtered.filter(c => c.isGuaranteed === true);
    } else if (guaranteeFilter === 'non-guaranteed') {
      filtered = filtered.filter(c => c.isGuaranteed === false);
    }
    // 'all' - no filter applied
    
    // Apply search filter (searches in both Seller ID and Seller Name)
    if (searchSeller) {
      const searchTerm = searchSeller.toLowerCase();
      filtered = filtered.filter(c => 
        c.sellerId?.toString().toLowerCase().includes(searchTerm) ||
        c.sellerName?.toLowerCase().includes(searchTerm)
      );
    }
    
    return filtered;
  };

  // Calculate total amount
  const getTotalAmount = () => {
    const filtered = getFilteredCredits();
    return filtered.reduce((sum, credit) => sum + credit.amount, 0);
  };

  // Initialize with current user's ID - only show user's own payment guarantees
  useEffect(() => {
    if (!currentUserId) return;

    setCustomerId(currentUserId.toString());
    fetchCustomerInfo(currentUserId);
  }, [currentUserId]);

  // Load temp incoming credits from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('tempIncomingCredits');
    if (stored) {
      try {
        const tempCredits = JSON.parse(stored);
        setTempIncomingCredits(tempCredits);
        // Clear localStorage after loading (so refresh clears them)
        localStorage.removeItem('tempIncomingCredits');
      } catch (e) {
        console.error('Error loading temp credits:', e);
      }
    }
  }, []);

  // Listen for temp incoming payment approvals (real-time events)
  useEffect(() => {
    const handleIncomingApproval = (event) => {
      const detail = event.detail;
      if (!detail) return;
      setTempIncomingCredits(prev => [detail, ...prev]);
      toast.success('Payment added to Pending tab.');
    };
    window.addEventListener(INCOMING_PAYMENT_APPROVED_EVENT, handleIncomingApproval);
    return () => window.removeEventListener(INCOMING_PAYMENT_APPROVED_EVENT, handleIncomingApproval);
  }, []);

  const filteredCredits = getFilteredCredits();

  return (
    <Box sx={{ 
      minHeight: '100vh',
      py: 4,
      px: { xs: 2, sm: 3 }
    }}>
      <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
        
        {/* Main Card */}
        <Card elevation={24} sx={{ 
          borderRadius: 4,
          overflow: 'hidden'
        }}>
          
          {/* Header Banner */}
          <Box sx={{ 
            background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
            p: 3,
            color: 'white'
          }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <Avatar sx={{ 
                  width: 64, 
                  height: 64,
                  background: 'rgba(255,255,255,0.25)',
                  border: '3px solid rgba(255,255,255,0.3)',
                  fontSize: '1.5rem'
                }}>
                 {user?.name && user?.surname ? 
                    `${user.name.charAt(0)}${user.surname.charAt(0)}`.toUpperCase() : 
                    user?.name ? user.name.charAt(0).toUpperCase() : 'U'}
               
                </Avatar>
              </Grid>
              <Grid item xs>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                {customerName}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Customer ID: {customerId}
                </Typography>
              </Grid>
              <Grid item xs={12} sm="auto">
                <Grid container spacing={2}>
                  <Grid item xs={6} sm="auto">
                    <Box sx={{ textAlign: { xs: 'center', sm: 'right' } }}>
                      <Typography variant="caption" sx={{ opacity: 0.9, display: 'block' }}>
                        Line of Credit
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {customerLimit}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm="auto">
                    <Box sx={{ textAlign: { xs: 'center', sm: 'right' } }}>
                      <Typography variant="caption" sx={{ opacity: 0.9, display: 'block' }}>
                        Available Limit
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {availableLimit}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <Refresh />}
                    onClick={() => fetchCustomerInfo(customerId)}
                    disabled={!customerId || loading}
                    sx={{
                      background: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      '&:hover': {
                        background: 'rgba(255,255,255,0.3)',
                      }
                    }}
                  >
                    {loading ? 'Loading...' : 'Refresh'}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => navigate('/user/new-credit')}
                    disabled={!customerId}
                    sx={{
                      background: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      '&:hover': {
                        background: 'rgba(255,255,255,0.3)',
                      }
                    }}
                  >
                    New Payment
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Content */}
          <CardContent sx={{ p: 4 }}>
            
            {/* Error Alert */}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            {/* Guarantee Filter */}
            <Box sx={{ mb: 3, display: 'flex', gap: 1, alignItems: 'center' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mr: 1 }}>
                Filter:
              </Typography>
              <Chip
                label="All"
                onClick={() => setGuaranteeFilter('all')}
                color={guaranteeFilter === 'all' ? 'primary' : 'default'}
                size="small"
                sx={{ cursor: 'pointer' }}
              />
              <Chip
                label="Guaranteed"
                onClick={() => setGuaranteeFilter('guaranteed')}
                color={guaranteeFilter === 'guaranteed' ? 'primary' : 'default'}
                size="small"
                icon={<Star sx={{ fontSize: '0.875rem !important' }} />}
                sx={{ cursor: 'pointer' }}
              />
              <Chip
                label="Non-Guaranteed"
                onClick={() => setGuaranteeFilter('non-guaranteed')}
                color={guaranteeFilter === 'non-guaranteed' ? 'primary' : 'default'}
                size="small"
                sx={{ cursor: 'pointer' }}
              />
            </Box>

            {/* Risk Statistics Cards */}
            {risks.length > 0 && (
              <Box sx={{ 
                mb: 4,
                display: 'grid',
                gap: { xs: 2, sm: 2, md: 3 },
                gridTemplateColumns: {
                  xs: 'repeat(1, minmax(0, 1fr))',
                  sm: 'repeat(2, minmax(0, 1fr))',
                  md: 'repeat(3, minmax(0, 1fr))',
                },
                gridAutoRows: '1fr'
              }}>
                <Card elevation={8} sx={{ 
                  borderRadius: 4,
                  transition: 'all 0.3s ease',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
                  }
                }}>
                  <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ 
                        p: 1.5, 
                        borderRadius: 3, 
                        backgroundColor: 'rgba(25, 118, 210, 0.1)',
                        mr: 2
                      }}>
                        <CreditCard sx={{ color: 'primary.main', fontSize: 28 }} />
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        Number of Credits
                      </Typography>
                    </Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
                      {getRiskStats().total}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatCurrency(getRiskStats().totalAmount, 'EUR')}
                    </Typography>
                  </CardContent>
                </Card>

                <Card elevation={8} sx={{ 
                  borderRadius: 4,
                  transition: 'all 0.3s ease',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
                  }
                }}>
                  <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ 
                        p: 1.5, 
                        borderRadius: 3, 
                        backgroundColor: 'rgba(255, 152, 0, 0.1)',
                        mr: 2
                      }}>
                        <CheckCircle sx={{ color: 'warning.main', fontSize: 28 }} />
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        Active
                      </Typography>
                    </Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'warning.main', mb: 1 }}>
                      {getRiskStats().active}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatCurrency(getRiskStats().activeAmount, 'EUR')}
                    </Typography>
                  </CardContent>
                </Card>

                <Card elevation={8} sx={{ 
                  borderRadius: 4,
                  transition: 'all 0.3s ease',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
                  }
                }}>
                  <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ 
                        p: 1.5, 
                        borderRadius: 3, 
                        backgroundColor: 'rgba(76, 175, 80, 0.1)',
                        mr: 2
                      }}>
                        <CheckCircle sx={{ color: 'success.main', fontSize: 28 }} />
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        Closed
                      </Typography>
                    </Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main', mb: 1 }}>
                      {getRiskStats().closed}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Closed credits
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            )}

            {/* Search */}
            <Box sx={{ mb: 3, p: 3, backgroundColor: 'grey.50', borderRadius: 3 }}>
              <TextField
                fullWidth
                placeholder="Search by Seller ID or Name..."
                value={searchSeller}
                onChange={(e) => setSearchSeller(e.target.value)}
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search color="primary" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    '&:hover fieldset': { borderColor: '#1976d2' },
                    '&.Mui-focused fieldset': { borderColor: '#1976d2' }
                  }
                }}
              />
            </Box>

            {/* Credits Table */}
            <Card elevation={8} sx={{ borderRadius: 3 }}>
              {/* Tabs */}
              <Box sx={{ 
                borderBottom: 1, 
                borderColor: 'divider', 
                px: 3, 
                pt: 3,
                background: 'linear-gradient(to right, rgba(25, 118, 210, 0.02) 0%, rgba(25, 118, 210, 0.05) 100%)'
              }}>
                <Tabs 
                  value={activeTab} 
                  onChange={handleTabChange}
                  sx={{
                    '& .MuiTab-root': {
                      textTransform: 'none',
                      fontSize: '1rem',
                      fontWeight: 500,
                      minHeight: 48,
                      '&.Mui-selected': {
                        color: '#1976d2',
                        fontWeight: 600,
                      }
                    },
                    '& .MuiTabs-indicator': {
                      backgroundColor: '#1976d2',
                      height: 3,
                      borderRadius: '3px 3px 0 0'
                    }
                  }}
                >
                  <Tab 
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PendingActions fontSize="small" />
                        Pending
                        <Chip 
                          label={credits.filter(c => c.status.toLowerCase() === 'pending').length} 
                          size="small" 
                          color="warning"
                          sx={{ 
                            height: 20,
                            fontSize: '0.75rem',
                            fontWeight: 600
                          }}
                        />
                      </Box>
                    }
                  />
                  <Tab 
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircle fontSize="small" />
                        Approved
                        <Chip 
                          label={credits.filter(c => c.status.toLowerCase() === 'active').length} 
                          size="small" 
                          color="success"
                          sx={{ 
                            height: 20,
                            fontSize: '0.75rem',
                            fontWeight: 600
                          }}
                        />
                      </Box>
                    }
                  />
                  <Tab 
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Cancel fontSize="small" />
                        Rejected
                        <Chip 
                          label={credits.filter(c => c.status.toLowerCase() === 'rejected').length} 
                          size="small" 
                          color="error"
                          variant="outlined"
                          sx={{ 
                            height: 20,
                            fontSize: '0.75rem',
                            fontWeight: 600
                          }}
                        />
                      </Box>
                    }
                  />
                </Tabs>
              </Box>

              {/* Table */}
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.875rem' }}>Payment Number</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.875rem' }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.875rem' }}>Seller ID</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.875rem' }}>Seller Name</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.875rem' }}>Transferred To</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.875rem' }}>Create Date</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.875rem' }}>Payment Date</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.875rem' }}>Credit Type</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.875rem' }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'right', fontSize: '0.875rem' }}>Amount</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'center', fontSize: '0.875rem' }}>View</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={11} sx={{ textAlign: 'center', py: 8 }}>
                          <CircularProgress size={40} sx={{ color: '#1976d2' }} />
                          <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary', fontSize: '0.875rem' }}>
                            Loading credit information...
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : filteredCredits.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={11} sx={{ textAlign: 'center', py: 8 }}>
                          <Box sx={{ 
                            display: 'inline-flex', 
                            p: 3, 
                            borderRadius: 3, 
                            backgroundColor: 'grey.100',
                            mb: 2
                          }}>
                            <Receipt sx={{ fontSize: 48, color: 'grey.400' }} />
                          </Box>
                          <Typography variant="h6" sx={{ mb: 1, color: 'text.primary', fontSize: '1rem' }}>
                            No Guarantees Found
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                            {customerId ? 'No credits found for this customer.' : 'Enter a Customer ID to view guarantees.'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredCredits.map((credit, index) => {
                        const creditWithRisk = creditsWithRisks.find(c => c.id === credit.id);
                        const firstRisk = creditWithRisk?.risks?.[0];
                        const riskLabel = firstRisk
                          ? getRiskTypeText(firstRisk.riskType)
                          : (credit.tempCreditType || '-');
                        const riskColor = firstRisk
                          ? firstRisk.riskType === 1 ? 'primary' : 'secondary'
                          : (credit.tempCreditType ? 'info' : 'default');
                        return (
                          <TableRow 
                            key={credit.id} 
                            hover
                            sx={{
                              '&:hover': {
                                backgroundColor: 'rgba(25, 118, 210, 0.04)',
                              }
                            }}
                          >
                        <TableCell sx={{ fontWeight: 600, color: 'primary.main', fontSize: '0.8rem' }}>
                          <Typography sx={{ fontSize: '0.8rem' }}>
                            {credit.paymentNumber || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>
                          {credit.isGuaranteed ? (
                            <Chip
                              label="Guaranteed"
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: '0.65rem',
                                backgroundColor: '#FFC107',
                                color: '#000',
                                fontWeight: 600,
                              }}
                              icon={<Star sx={{ fontSize: '0.75rem !important', color: '#000 !important' }} />}
                            />
                          ) : (
                            <Chip
                              label="Non-Guaranteed"
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: '0.65rem',
                                backgroundColor: '#E0E0E0',
                                color: '#666',
                                fontWeight: 500,
                              }}
                            />
                          )}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 500, fontSize: '0.8rem' }}>{credit.sellerId}</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.8rem' }}>{credit.sellerName}</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: 'info.main', fontSize: '0.8rem' }}>
                          {credit.hasTransfers && credit.currentSellerName ? credit.currentSellerName : '-'}
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                          {credit.createdOn ? new Date(credit.createdOn).toLocaleDateString('en-US') : '-'}
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                          {credit.validityDate ? new Date(credit.validityDate).toLocaleDateString('en-US') : '-'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>
                          {riskLabel !== '-' ? (
                            <Chip
                              label={riskLabel}
                              size="small"
                              color={riskColor}
                              sx={{ 
                                fontWeight: 600, 
                                borderRadius: 3, 
                                color: 'white',
                                fontSize: '0.75rem',
                                height: 24
                              }}
                            />
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>
                          <Chip
                            label={getStatusText(credit.status)}
                            color={getStatusColor(credit.status)}
                            size="small"
                            sx={{ 
                              fontWeight: 600,
                              borderRadius: 3,
                              color:"white",
                              fontSize: '0.75rem',
                              height: 24
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ 
                          textAlign: 'right', 
                          fontWeight: 700,
                          color: '#1976d2',
                          fontSize: '0.8rem'
                        }}>
                          {formatCurrency(credit.amount, credit.currency)}
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center', fontSize: '0.8rem' }}>
                          <Tooltip title="View Check Image">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={async () => {
                                setSelectedCredit(credit);
                                setModalTab(0); // Reset to View tab
                                setCheckDialog(true);
                                // Check if transfer history exists for buyer
                                if (currentUserId === credit.buyerId && credit.status?.toLowerCase() === 'active') {
                                  const historyResult = await CreditService.getTransferHistory(credit.id);
                                  setHasTransferHistory(historyResult.success && historyResult.data && historyResult.data.length > 0);
                                } else {
                                  setHasTransferHistory(false);
                                }
                              }}
                              sx={{
                                '&:hover': {
                                  backgroundColor: 'primary.lighter',
                                }
                              }}
                            >
                              <Image />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Footer with Total */}
          {filteredCredits.length > 0 && (
            <Box sx={{ 
              p: 3, 
              borderTop: 2, 
              borderColor: 'divider',
              background: 'linear-gradient(to right, rgba(25, 118, 210, 0.02) 0%, rgba(25, 118, 210, 0.05) 100%)'
            }}>
              <Grid container alignItems="center" spacing={2}>
                <Grid item xs>
                  <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.secondary', fontSize: '0.875rem' }}>
                    {activeTab === 0 ? 'Pending' : activeTab === 1 ? 'Approved' : 'Rejected'} Total Amount:
                  </Typography>
                </Grid>
                <Grid item>
                  <Paper sx={{ 
                    px: 3, 
                    py: 1.5, 
                    backgroundColor: '#1976d2',
                    color: 'white',
                    borderRadius: 3
                  }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, fontSize: '1.25rem' }}>
                      {formatCurrency(getTotalAmount(), filteredCredits[0]?.currency || credits[0]?.currency || tempIncomingCredits[0]?.currency || 'EUR')}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
            </Card>
          </CardContent>
        </Card>

        {/* Check Image Dialog */}
        <Dialog
          open={checkDialog}
          onClose={() => {
            setCheckDialog(false);
            setSelectedCredit(null);
            setModalTab(0); // Reset to View tab when closing
            setIsCheckFlipped(false); // Reset flip state
            setHasTransferHistory(false);
          }}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
            }
          }}
        >
          <DialogTitle
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              color: 'white',
              py: 2.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontSize: '1.5rem',
              fontWeight: 'bold',
            }}
          >
            {((selectedCredit?.tempCreditType === 'Payment Guarantee' || selectedCredit?.tempCreditType === 'Payment') ||
              (selectedCredit?.creditType === 'PaymentGuarantee' || selectedCredit?.creditType === 1)) ? 'Payment Details' : 'View'}
            {selectedCredit && selectedCredit.status?.toLowerCase() === 'active' && modalTab === 0 && (
              (currentUserId === selectedCredit.sellerId || 
               (currentUserId === selectedCredit.buyerId && selectedCredit.status?.toLowerCase() === 'active' && hasTransferHistory)) && (
                <Button
                  variant={isCheckFlipped ? 'contained' : 'outlined'}
                  startIcon={<Flip />}
                  onClick={() => setIsCheckFlipped(!isCheckFlipped)}
                  sx={{
                    background: isCheckFlipped 
                      ? 'rgba(255, 255, 255, 0.2)' 
                      : 'transparent',
                    color: 'white',
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 0.3)',
                    },
                  }}
                >
                  {currentUserId === selectedCredit.sellerId ? 'Transfer Receivable' : 'Transfer History'}
                </Button>
              )
            )}
          </DialogTitle>
          
          {/* Tabs for Payments */}
          {((selectedCredit?.tempCreditType === 'Payment Guarantee' || selectedCredit?.tempCreditType === 'Payment') ||
            (selectedCredit?.creditType === 'PaymentGuarantee' || selectedCredit?.creditType === 1)) && (
            <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
              <Tabs
                value={modalTab}
                onChange={(e, newValue) => setModalTab(newValue)}
                sx={{
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 600,
                  },
                }}
              >
                <Tab label="View" />
                <Tab label="Invoice" />
              </Tabs>
            </Box>
          )}

          <DialogContent sx={{ p: 3 }}>
            {selectedCredit && (
              <>
                {((selectedCredit.tempCreditType === 'Payment Guarantee' || selectedCredit.tempCreditType === 'Payment') ||
                  (selectedCredit.creditType === 'PaymentGuarantee' || selectedCredit.creditType === 1)) && modalTab === 1 ? (
                  // Invoice Tab - Show invoice image
                  (() => {
                    // Get invoice image from localStorage
                    const invoiceImages = JSON.parse(localStorage.getItem('invoiceImages') || '{}');
                    const invoiceImage = selectedCredit.paymentNumber ? invoiceImages[selectedCredit.paymentNumber] : null;
                    
                    return (
                      <Box
                        sx={{
                          border: '1px solid #e0e0e0',
                          borderRadius: 3,
                          p: 2,
                          backgroundColor: '#fafafa',
                          textAlign: 'center',
                          minHeight: 400,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {invoiceImage ? (
                          <Box sx={{ width: '100%', maxWidth: 800 }}>
                            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                              Invoice Document
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              Payment Number: {selectedCredit.paymentNumber || 'N/A'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                              Amount: {formatCurrency(selectedCredit.amount || 0, selectedCredit.currency || 'EUR')}
                            </Typography>
                            <Box
                              sx={{
                                mt: 2,
                                border: '1px solid #e0e0e0',
                                borderRadius: 2,
                                overflow: 'hidden',
                                bgcolor: 'white',
                              }}
                            >
                              <img
                                src={invoiceImage}
                                alt="Invoice"
                                style={{
                                  width: '100%',
                                  height: 'auto',
                                  display: 'block',
                                }}
                              />
                            </Box>
                          </Box>
                        ) : (
                          <Box>
                            <Receipt sx={{ fontSize: 80, color: 'grey.400', mb: 2 }} />
                            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                              Invoice Document
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Payment Number: {selectedCredit.paymentNumber || 'N/A'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              Amount: {formatCurrency(selectedCredit.amount || 0, selectedCredit.currency || 'EUR')}
                            </Typography>
                            <Box
                              sx={{
                                mt: 3,
                                p: 3,
                                bgcolor: 'white',
                                borderRadius: 3,
                                border: '2px dashed #e0e0e0',
                                maxWidth: 500,
                                mx: 'auto',
                              }}
                            >
                              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                No invoice image available
                              </Typography>
                              <Box
                                sx={{
                                  mt: 2,
                                  height: 200,
                                  bgcolor: 'grey.100',
                                  borderRadius: 1,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <Image sx={{ fontSize: 60, color: 'grey.400' }} />
                              </Box>
                            </Box>
                          </Box>
                        )}
                      </Box>
                    );
                  })()
                ) : (
                  // View Tab - Show check image (for all credits, including invoice payment)
              <Box
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 3,
                  p: 2,
                  backgroundColor: '#fafafa',
                }}
              >
                <CheckImage
                  ref={checkImageRef}
                  checkData={{
                    amount: parseFloat(selectedCredit.amount) || 0,
                    currency: selectedCredit.currency || 'EUR',
                    buyerName: customerName || selectedCredit.buyerName || 'Buyer Name',
                    sellerName: selectedCredit.sellerName || 'Seller Name', // Original seller name (from backend)
                    buyerIban: selectedCredit.buyerIban || 'TR00 0000 0000 0000 0000 0000 00',
                    dueDate: selectedCredit.validityDate,
                    checkNumber: selectedCredit.paymentNumber || '',
                    paymentNumber: selectedCredit.paymentNumber || '',
                    isGuaranteed: selectedCredit.isGuaranteed || false,
                    description: selectedCredit.description || '',
                    creatorName: user?.name && user?.surname ? `${user.name} ${user.surname}` : user?.name || '',
                  }}
                  preview={true}
                  showTemplate={true}
                  enableTransfer={selectedCredit.status?.toLowerCase() === 'active' && currentUserId === selectedCredit.sellerId}
                  showTransferHistory={selectedCredit.status?.toLowerCase() === 'active' && currentUserId === selectedCredit.buyerId}
                  creditId={selectedCredit.id}
                  currentUserId={currentUserId}
                  currentSellerId={selectedCredit.sellerId}
                  currentBuyerId={selectedCredit.buyerId}
                  isFlipped={isCheckFlipped}
                  onFlipChange={(flipped) => setIsCheckFlipped(flipped)}
                  onSelectedCustomerChange={(customers) => {
                    setSelectedCustomerForTransfer(customers);
                  }}
                  myPayments={credits.filter(c => c.status?.toLowerCase() === 'active')}
                  onTransferApprove={(transferData) => {
                    const sellerNames = transferData.partialTransfers 
                      ? transferData.partialTransfers.map(pt => pt.newSellerName).join(', ')
                      : transferData.newSellerName;
                    toast.success(`Credit transferred successfully to ${sellerNames}. The payment is now assigned to ${sellerNames} instead of the previous seller.`);
                    setSelectedCustomerForTransfer(null);
                    setIsTransferring(false);
                    setIsCheckFlipped(false);
                    setCheckDialog(false);
                    setHasTransferHistory(false);
                    // Refresh credits list - buyer's list remains the same
                    if (currentUserId) {
                      fetchCustomerInfo(currentUserId);
                    }
                  }}
                />
              </Box>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2.5, pt: 0 }}>
            {selectedCredit && selectedCredit.status?.toLowerCase() === 'active' && 
             isCheckFlipped && 
             (currentUserId === selectedCredit.sellerId || (currentUserId === selectedCredit.buyerId && selectedCredit.status?.toLowerCase() === 'active')) ? (
              <>
                <Button
                  onClick={() => {
                    setIsCheckFlipped(false);
                    setSelectedCustomerForTransfer(null);
                  }}
                  variant="outlined"
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 2,
                  }}
                >
                  Cancel
                </Button>
                {currentUserId === selectedCredit.sellerId && (
                  <Button
                    onClick={async () => {
                      if (checkImageRef.current) {
                        setIsTransferring(true);
                        try {
                          await checkImageRef.current.approve();
                        } catch (error) {
                          console.error('Transfer error:', error);
                        } finally {
                          setIsTransferring(false);
                        }
                      }
                    }}
                    variant="contained"
                    startIcon={isTransferring ? <CircularProgress size={20} color="inherit" /> : <CheckCircle />}
                    disabled={!selectedCustomerForTransfer || (Array.isArray(selectedCustomerForTransfer) && selectedCustomerForTransfer.length === 0) || isTransferring}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
                      },
                      '&:disabled': {
                        background: '#cbd5e1',
                        color: '#94a3b8',
                      },
                    }}
                  >
                    {isTransferring ? 'Transferring...' : 'Approve Transfer'}
                  </Button>
                )}
              </>
            ) : (
              <Button
                onClick={() => {
                  setCheckDialog(false);
                  setSelectedCredit(null);
                  setModalTab(0);
                  setIsCheckFlipped(false);
                  setSelectedCustomerForTransfer(null);
                  setHasTransferHistory(false);
                }}
                variant="contained"
                color="primary"
                sx={{ borderRadius: 3 }}
              >
                Close
              </Button>
            )}
          </DialogActions>
        </Dialog>

      </Box>
    </Box>
  );
};

export default Credits;